import { generateDeviceFingerprint } from '../utils/deviceFingerprint';
import { storage } from './storage';
import { API_BASE_URL } from '../utils/api'; // Environment-aware API URL

export interface PendingUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  inviteUrl: string;
  inviteToken: string;
  createdAt: Date;
  expiresAt: Date;
  status: 'pending' | 'registered' | 'expired';
  deviceFingerprint?: string;
  firstLoginAt?: Date;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
}

export class AdminUserService {
  private static readonly INVITE_EXPIRY_HOURS = 720; // 30 days for beta period - manual termination only

  private static readonly API_BASE = `${API_BASE_URL}/api/admin`;

  /**
   * Creates a new user with enhanced system
   */
  static async createUser(userData: CreateUserRequest & { adminNotes?: string }): Promise<any> {
    try {
      const response = await fetch(`${this.API_BASE}/users/create`, await this.getFetchOptions('POST', userData));

      if (!response.ok) {
        throw new Error(`Failed to create user: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result.user;
    } catch (error) {
      return await this.handleNetworkError(error, 'createUser');
    }
  }

  /**
   * Creates a new pending user with secure invite link (legacy)
   */
  static async createPendingUser(userData: CreateUserRequest): Promise<PendingUser> {
    try {
      // Use enhanced endpoint but maintain compatibility
      const result = await this.createUser(userData);
      
      // Convert to legacy format
      const pendingUser: PendingUser = {
        id: result.id,
        firstName: result.firstName,
        lastName: result.lastName,
        email: result.email,
        inviteUrl: result.inviteUrl,
        inviteToken: result.inviteToken,
        createdAt: new Date(result.createdAt),
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
        status: result.status as 'pending' | 'registered' | 'expired'
      };

      return pendingUser;
    } catch (error) {
      return await this.handleNetworkError(error, 'createPendingUser');
    }
  }

  /**
   * Gets all users (enhanced version)
   */
  static async getAllUsers(filter: string = 'all'): Promise<any[]> {
    try {
      const response = await fetch(`${this.API_BASE}/users/all?include_status=${filter}`, await this.getFetchOptions('GET'));

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.users || [];
    } catch (error) {
      console.error('Error fetching all users:', error);
      throw error;
    }
  }

  /**
   * Gets all pending users (legacy compatibility)
   */
  static async getPendingUsers(): Promise<PendingUser[]> {
    try {
      const response = await fetch(`${this.API_BASE}/users/pending`, await this.getFetchOptions('GET'));

      if (!response.ok) {
        throw new Error(`Failed to fetch pending users: ${response.status} ${response.statusText}`);
      }

      const backendUsers = await response.json();
      
      // Convert backend response to frontend format
      return backendUsers.map((user: any) => ({
        ...user,
        createdAt: new Date(user.createdAt),
        expiresAt: new Date(user.expiresAt),
        firstLoginAt: user.firstLoginAt ? new Date(user.firstLoginAt) : undefined
      }));
    } catch (error) {
      console.error('Error fetching pending users:', error);
      throw error;
    }
  }

  /**
   * Validates an invite token and registers device
   */
  static async validateInvite(token: string): Promise<{
    valid: boolean;
    user?: PendingUser;
    deviceFingerprint?: string;
    error?: string;
  }> {
    const deviceFingerprint = await generateDeviceFingerprint();
    
    try {
      const response = await fetch(`${this.API_BASE}/users/validate-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token, deviceFingerprint })
      });

      if (!response.ok) {
        throw new Error(`Failed to validate invite: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error validating invite:', error);
      return {
        valid: false,
        error: 'Failed to validate invite - please try again'
      };
    }
  }

  /**
   * Marks a user as registered
   */
  static async markUserRegistered(token: string): Promise<void> {
    try {
      const response = await fetch(`${this.API_BASE}/users/mark-registered`, await this.getFetchOptions('POST', { token }));

      if (!response.ok) {
        throw new Error(`Failed to mark user registered: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error marking user registered:', error);
      throw error;
    }
  }

  /**
   * Revokes an invite
   */
  static async revokeInvite(userId: string): Promise<void> {
    try {
      const response = await fetch(`${this.API_BASE}/users/${userId}/revoke`, await this.getFetchOptions('DELETE'));

      if (!response.ok) {
        throw new Error(`Failed to revoke invite: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error revoking invite:', error);
      throw error;
    }
  }

  /**
   * Regenerates an invite for a user
   */
  static async regenerateInvite(userId: string): Promise<{
    inviteToken: string;
    inviteUrl: string;
    expiresAt: Date;
  }> {
    try {
      const response = await fetch(`${this.API_BASE}/users/${userId}/regenerate`, await this.getFetchOptions('POST', {}));

      if (!response.ok) {
        throw new Error(`Failed to regenerate invite: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      return {
        inviteToken: result.inviteToken,
        inviteUrl: result.inviteUrl,
        expiresAt: new Date(result.expiresAt)
      };
    } catch (error) {
      console.error('Error regenerating invite:', error);
      throw error;
    }
  }

  /**
   * Grants beta tester access to a user (free extended tier)
   */
  static async grantBetaAccess(userId: string, notes: string = ''): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE}/beta-users/grant-access`,
        await this.getFetchOptions('POST', { user_id: userId, notes, granted_by: 'Admin Portal' })
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to grant beta access: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return await this.handleNetworkError(error, 'grantBetaAccess');
    }
  }

  /**
   * Revokes beta tester access from a user
   */
  static async revokeBetaAccess(userId: string, notes: string = ''): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE}/beta-users/revoke-access`,
        await this.getFetchOptions('POST', { user_id: userId, reason: notes, revoked_by: 'Admin Portal' })
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to revoke beta access: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return await this.handleNetworkError(error, 'revokeBetaAccess');
    }
  }

  /**
   * Sets admin tier override for a user (for testing different subscription tiers)
   */
  static async setTierOverride(userId: string, overrideTier: string | null, notes: string = ''): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE}/beta-users/set-tier-override`,
        await this.getFetchOptions('POST', {
          user_id: userId,
          override_tier: overrideTier,
          notes,
          set_by: 'Admin Portal'
        })
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to set tier override: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      return await this.handleNetworkError(error, 'setTierOverride');
    }
  }

  /**
   * Pre-grant eligibility and impact check. Returns structured data
   * for the multi-step modal preview before any write occurs.
   */
  static async evaluationPreflight(userId: string): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE}/beta-users/evaluation/preflight?user_id=${encodeURIComponent(userId)}`,
        await this.getFetchOptions('GET')
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const msg = typeof detail === 'object' ? detail.message : detail;
        throw new Error(msg || `Preflight check failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      return await this.handleNetworkError(error, 'evaluationPreflight');
    }
  }

  /**
   * Grant Full and Free Evaluation (Pro) — configurable promotional entitlement.
   * Eligible: pre-trial, active trial, or first month of paid subscription.
   * @param reason - platform_issue | early_adopter | extended_trial | feedback | onboarding_recovery
   * @param duration - three_day | weekly | monthly | two_month | three_month | six_month | yearly | lifetime
   */
  static async grantEvaluation(userId: string, reason: string = 'early_adopter', duration: string = '', notes: string = ''): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE}/beta-users/grant-evaluation`,
        await this.getFetchOptions('POST', { user_id: userId, reason, duration, notes, granted_by: 'Admin Portal' })
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const msg = typeof detail === 'object' ? detail.message : detail;
        throw new Error(msg || `Failed to grant evaluation: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      return await this.handleNetworkError(error, 'grantEvaluation');
    }
  }

  /**
   * Revoke Full and Free Evaluation — restores user to pre-evaluation subscription state.
   */
  static async revokeEvaluation(userId: string, reason: string = ''): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE}/beta-users/revoke-evaluation`,
        await this.getFetchOptions('POST', { user_id: userId, reason, revoked_by: 'Admin Portal' })
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const msg = typeof detail === 'object' ? detail.message : detail;
        throw new Error(msg || `Failed to revoke evaluation: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      return await this.handleNetworkError(error, 'revokeEvaluation');
    }
  }

  /**
   * Add more time to an active free trial extension.
   * @param duration - three_day | weekly | monthly | two_month | three_month | six_month | yearly | lifetime
   * Note: granted_by/extended_by/revoked_by in the request body is overridden
   * server-side with the authenticated admin's email — the value passed here
   * is kept for backward compatibility but not authoritative.
   */
  static async extendEvaluation(userId: string, duration: string = 'two_month', notes: string = ''): Promise<any> {
    try {
      const response = await fetch(
        `${this.API_BASE}/beta-users/extend-evaluation`,
        await this.getFetchOptions('POST', { user_id: userId, duration, notes, extended_by: 'Admin Portal' })
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        const msg = typeof detail === 'object' ? detail.message : detail;
        throw new Error(msg || `Failed to extend evaluation: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      return await this.handleNetworkError(error, 'extendEvaluation');
    }
  }

  // Helper methods for token generation
  private static generateSecureToken(): string {
    // Generate cryptographically secure random token
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return 'invite_' + Array.from(array, byte => byte.toString(36)).join('').substring(0, 9);
  }

  private static async getAdminToken(): Promise<string> {
    // Prefer HttpOnly cookie on cross-origin; fallback to storage token if present
    // Note: HttpOnly cookie cannot be read from JS; presence is inferred by using credentials: 'include'.
    return await storage.getItem('admin_token') || '';
  }

  private static getCSRFToken(): string | null {
    // Try common CSRF cookie name or meta tag
    const match = document.cookie.match(/(?:^|; )csrf_token=([^;]*)/);
    if (match) return decodeURIComponent(match[1]);
    const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
    return meta?.content || null;
  }

  private static async getAuthHeaders(): Promise<{ [key: string]: string }> {
    const headers: { [key: string]: string } = { 'Content-Type': 'application/json' };
    const token = await this.getAdminToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const csrf = this.getCSRFToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    headers['X-Admin-Client'] = 'firebase-portal';
    return headers;
  }

  private static async getFetchOptions(method: string = 'GET', body?: any): Promise<RequestInit> {
    const token = await this.getAdminToken();
    const options: RequestInit = {
      method,
      headers: await this.getAuthHeaders()
    };

    // Always include credentials to allow HttpOnly cookie auth when available
    options.credentials = 'include';

    if (body) {
      options.body = JSON.stringify(body);
    }

    return options;
  }

  /**
   * Enhanced error handling for network issues
   */
  private static async handleNetworkError(error: any, endpoint: string): Promise<never> {
    console.error(`[AdminUserService] Network error for ${endpoint}:`, error);
    
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error(`Network connection failed. This might be due to:
1. SSL certificate issues with the backend server
2. CORS configuration problems  
3. Backend server being unavailable
4. Firewall or network restrictions

Please try refreshing the page or contact support if the issue persists.`);
    }
    
    throw error;
  }
} 